import { useParams, Link } from 'react-router-dom';
import { useMemo, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { DocsSidebarNav, SidebarSection } from '@/components/docs/DocsSidebarNav';
import { useFieldLibraryDocs, useFieldDoc } from '@/hooks/useFieldLibraryDocs';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Database, Hash, Type, ListOrdered, ToggleLeft, Box, FileText, ChevronLeft, ChevronRight } from 'lucide-react';

const typeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  string: Type,
  number: Hash,
  integer: ListOrdered,
  boolean: ToggleLeft,
  array: Box,
  object: FileText,
};

const typeOrder = ['string', 'number', 'integer', 'boolean', 'array', 'object'];

export default function FieldArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { data: field, isLoading, error } = useFieldDoc(slug || '');
  const { data: allFieldsData } = useFieldLibraryDocs();
  const { trackDocView } = useAnalytics();

  // Track field view
  useEffect(() => {
    if (field && slug) {
      trackDocView('field', slug);
    }
  }, [field, slug, trackDocView]);

  // Build sidebar sections
  const sidebarSections: SidebarSection[] = useMemo(() => {
    if (!allFieldsData) return [];
    
    return typeOrder
      .filter((type) => allFieldsData.groupedByType[type]?.length > 0)
      .map((type) => ({
        title: type.charAt(0).toUpperCase() + type.slice(1),
        slug: type,
        items: allFieldsData.groupedByType[type]?.map((f) => ({
          title: f.name,
          slug: f.slug,
          href: `/docs/field-library/${f.slug}`,
        })) || [],
      }));
  }, [allFieldsData]);

  // Find prev/next fields
  const { prevField, nextField } = useMemo(() => {
    if (!allFieldsData || !slug) return { prevField: null, nextField: null };
    
    const allFields = allFieldsData.fields;
    const currentIndex = allFields.findIndex((f) => f.slug === slug);
    
    return {
      prevField: currentIndex > 0 ? allFields[currentIndex - 1] : null,
      nextField: currentIndex < allFields.length - 1 ? allFields[currentIndex + 1] : null,
    };
  }, [allFieldsData, slug]);

  const TypeIcon = field ? (typeIcons[field.data_type] || Type) : Database;

  return (
    <Layout>
      <SEOHead 
        path={`/docs/field-library/${slug}`}
        title={field ? `${field.name} Field | Field Library | AutomotiveAPI Docs` : undefined}
        description={field?.description || undefined}
      />
      
      <DocsNav />
      
      <div className="container py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <DocsSidebarNav sections={sidebarSections} className="hidden lg:block" />
          
          {/* Main Content */}
          <main className="flex-1 min-w-0 max-w-3xl">
            {/* Breadcrumb */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/docs">Docs</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/docs/field-library">Field Library</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {field && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>{field.name}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
            
            {/* Loading State */}
            {isLoading && (
              <div className="space-y-4">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="mt-8 space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Field not found</h2>
                <p className="text-muted-foreground mb-4">
                  The field you're looking for doesn't exist or has been moved.
                </p>
                <Button asChild variant="outline">
                  <Link to="/docs/field-library">Browse all fields</Link>
                </Button>
              </div>
            )}
            
            {/* Field Content */}
            {field && (
              <>
                <header className="mb-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <TypeIcon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-3xl font-bold">{field.name}</h1>
                        {field.is_common && (
                          <Badge variant="outline">common</Badge>
                        )}
                        <Badge variant="secondary">{field.data_type}</Badge>
                      </div>
                      <code className="text-sm text-muted-foreground">{field.slug}</code>
                    </div>
                  </div>
                  {field.description && (
                    <p className="text-lg text-muted-foreground mt-4">
                      {field.description}
                    </p>
                  )}
                </header>
                
                {/* Field Properties */}
                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle className="text-lg">Field Properties</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Data Type</dt>
                        <dd className="mt-1">
                          <Badge variant="secondary">{field.data_type}</Badge>
                        </dd>
                      </div>
                      
                      {field.format && (
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Format</dt>
                          <dd className="mt-1">
                            <code className="text-sm bg-muted px-2 py-1 rounded">{field.format}</code>
                          </dd>
                        </div>
                      )}
                      
                      {field.pattern && (
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-muted-foreground">Pattern</dt>
                          <dd className="mt-1">
                            <code className="text-sm bg-muted px-2 py-1 rounded font-mono break-all">{field.pattern}</code>
                          </dd>
                        </div>
                      )}
                      
                      {(field.min_length != null || field.max_length != null) && (
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Length Constraints</dt>
                          <dd className="mt-1 flex gap-2">
                            {field.min_length != null && (
                              <Badge variant="outline">min: {field.min_length}</Badge>
                            )}
                            {field.max_length != null && (
                              <Badge variant="outline">max: {field.max_length}</Badge>
                            )}
                          </dd>
                        </div>
                      )}
                      
                      {(field.minimum != null || field.maximum != null) && (
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Value Range</dt>
                          <dd className="mt-1 flex gap-2">
                            {field.minimum != null && (
                              <Badge variant="outline">min: {field.minimum}</Badge>
                            )}
                            {field.maximum != null && (
                              <Badge variant="outline">max: {field.maximum}</Badge>
                            )}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </CardContent>
                </Card>
                
                {/* Prev/Next Navigation */}
                <nav className="mt-12 pt-8 border-t flex items-center justify-between">
                  {prevField ? (
                    <Link 
                      to={`/docs/field-library/${prevField.slug}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <div className="text-right">
                        <div className="text-muted-foreground text-xs">Previous</div>
                        <div className="font-medium">{prevField.name}</div>
                      </div>
                    </Link>
                  ) : (
                    <div />
                  )}
                  
                  {nextField && (
                    <Link 
                      to={`/docs/field-library/${nextField.slug}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <div className="text-left">
                        <div className="text-muted-foreground text-xs">Next</div>
                        <div className="font-medium">{nextField.name}</div>
                      </div>
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  )}
                </nav>
              </>
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
}
